import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { FuelType } from '@allesauto/utils/consts';
import {
  getFuelConsumptionValue,
  getFueltypeTranslationKey,
  isWltpAttributeAvailable,
} from '@allesauto/utils/funcs.ts';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

interface UseConsumptionParams {
  fuelType?: SchemaTypes.Maybe<string> | undefined;
  electricConsumption: Maybe<number> | undefined;
  consumption: Maybe<number> | undefined;
  wltConsumption: Maybe<string> | undefined;
  wltConsumptionElectric: Maybe<string> | undefined;
  hasConfiguratorDealer: boolean;
  wltEmission: Maybe<number> | undefined;
}

export function useConsumption({
  fuelType,
  consumption,
  electricConsumption,
  wltEmission,
  wltConsumption,
  wltConsumptionElectric,
  hasConfiguratorDealer, // used to update values after configuration
}: UseConsumptionParams) {
  const intl = useIntl();

  const reformatVehicleConsumption = useCallback(
    (
      consumptionInput: string | number,
      fuelTypeInput?: SchemaTypes.Maybe<string> | undefined
    ) => {
      if (
        Number.parseFloat(consumptionInput + '')
          .toFixed(1)
          .includes('00')
      ) {
        return +consumptionInput / 1000;
      }

      if (fuelTypeInput) {
        return getFuelConsumptionValue(intl, fuelTypeInput, +consumptionInput);
      }

      return +consumptionInput;
    },
    [intl]
  );

  const consumptionValue = useMemo(() => {
    let value;
    switch (fuelType) {
      case FuelType.ELECTRIC:
        value =
          wltConsumptionElectric && wltConsumptionElectric !== '0'
            ? hasConfiguratorDealer
              ? electricConsumption
              : wltConsumptionElectric
            : electricConsumption;
        break;
      default:
        value =
          wltConsumption && wltConsumption !== '0'
            ? hasConfiguratorDealer
              ? consumption
              : wltConsumption
            : consumption;
        break;
    }

    if (value) {
      return translate(intl, getFueltypeTranslationKey(fuelType), {
        value: reformatVehicleConsumption(value, fuelType),
      });
    }

    return translate(intl, 'ui.COMMON.NOT_AVAILABLE');
  }, [
    fuelType,
    electricConsumption,
    consumption,
    wltConsumption,
    wltConsumptionElectric,
    hasConfiguratorDealer,
    intl,
    reformatVehicleConsumption,
  ]);

  const wltpAvailable = useMemo(() => {
    return (
      isWltpAttributeAvailable(String(wltConsumptionElectric)) ||
      isWltpAttributeAvailable(String(wltConsumption)) ||
      isWltpAttributeAvailable(String(wltEmission))
    );
  }, [wltConsumptionElectric, wltConsumption, wltEmission]);

  return { consumptionValue, wltpAvailable };
}
